import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { IChoiceGroupOption, IComboBoxOption } from "@fluentui/react";
import { FormTreeSelection } from "../../cpm/FormTree";
import { CoObjectItem, FormTable, SheetForm, TgkTableObjectSource } from "../../interfaces/CoObject";
import { ObjectPanelTabs } from "../co-panel/ObjectPanelComponent";
import { CheckboxChangeEvent } from "@progress/kendo-react-inputs";
import { SourceTypeEnum } from "../../../api/models/source-type-enum";
import { CoObjectSourcePanelStateData } from "./CoObjectSourceTablePanel";
import { DocumentPartMetadata } from "../../../api/models/document-part-metadata";

const sheetsAreaChartMap = new Map<string, string[]>();

const initialStateData: CoObjectSourcePanelStateData = {
  areasItems: [],
  loading: true,
};

export interface CoObjectSourceTableProps {
  documentPartMetadata: DocumentPartMetadata;
  objectSource: TgkTableObjectSource;
  updateCoObjectSourceTable: (obj: TgkTableObjectSource, tabValid, modified: boolean) => void;
  isEdit: boolean;
  isLocked: boolean;
  isComponentInstance: boolean;
  tab: ObjectPanelTabs;
  tableType: "TABLE" | "CHART";
  isFileExcelExisting?: boolean;
  fileExcelName?: string;
  updateFileExcelName?: (fileExcelName: string) => void;
  onPasteObject: (smartObject: CoObjectItem) => void;
  originalSheetArea?: SheetForm;
  setOriginalSheetArea?: Dispatch<SetStateAction<SheetForm>>;
  setIsOldSheetOrAreaSelected?: Dispatch<SetStateAction<boolean>>;
  setIsOldValueForAreaToHide?: Dispatch<SetStateAction<boolean>>;
  isOldValueForAreaToHide?: boolean;
  hasSheetToBeSelected?: boolean;
  setHasSheetToBeSelected?: Dispatch<SetStateAction<boolean>>;
  isLoadingDataExcel?: boolean;
  setIsLoadingDataExcel?: Dispatch<SetStateAction<boolean>>;
  setIsLoadingSheetAndArea?: Dispatch<SetStateAction<boolean>>;
  setIsSheetChartAndAreaToHide?: Dispatch<SetStateAction<boolean>>;
  isSheetChartAndAreaToHide?: boolean;
  objectSourceImage?: string;
  isImageUnchecked?: boolean;
}

const useCoObjectSourceTablePanel = (props: CoObjectSourceTableProps) => {
  const [stateData, setStateData] = React.useState<CoObjectSourcePanelStateData>(initialStateData);
  const [isOldValueForAreaToHide, setIsOldValueForAreaToHide] = useState<boolean>(true);
  const [hasSheetToBeSelected, setHasSheetToBeSelected] = useState<boolean>(false);
  const [isLoadingDataExcel, setIsLoadingDataExcel] = useState<boolean>(false);
  const [isImageUnchecked, setIsImageUnchecked] = useState<boolean>(false);

  const {
    documentPartMetadata,
    objectSource,
    updateCoObjectSourceTable,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
    isEdit,
    isComponentInstance,
    isLocked,
  } = props;
  const tableType = props.tableType ?? "TABLE";

  useEffect(() => {
    const objSource =
      objectSource == null
        ? ({
            //DEFAULT OBJSOURCE
            type: tableType,
            objectSource: "FORM",
            dbApplication: null,
            form: null,
            sheet: null,
            area: null,
            path: null,
            elabResultType: tableType == "CHART" ? "IMAGE_STRINGBASE64" : "STRINGBASE64",
            inheritFormatImage: true,
            formatImage: null,
          } as TgkTableObjectSource)
        : ({ ...objectSource, type: tableType } as TgkTableObjectSource);

    updateCoObjectSourceTable(objSource, validateFields(objSource), false);
  }, []);

  function initAreasOrCharts(sheet: SheetForm) {
    if (sheetsAreaChartMap) {
      sheetsAreaChartMap.forEach((value: string[], key: string) => {
        if (sheet && sheet.code == key) {
          const areaMap: IComboBoxOption[] = value?.map((area) => {
            return createIComboBoxFromAreas(area);
          });
          setStateData({
            ...stateData,
            areasItems: areaMap,
          });
        }
      });
    }
  }

  function createIComboBoxFromAreas(areaValue: string): IComboBoxOption {
    return {
      key: areaValue,
      text: areaValue,
    };
  }

  function selectDbComboBox(option: IComboBoxOption) {
    const obj = {
      ...objectSource,
      dbApplication: { id: String(option.key), desc: option.text },
      form: null,
      sheet: null,
      area: null,
      chart: null,
    } as TgkTableObjectSource;
    updateCoObjectSourceTable(obj, validateFields(obj), true);
  }

  const onFormClick = (formSelection: FormTreeSelection) => {
    sheetsAreaChartMap.clear();
    const formTable = {
      code: formSelection.formCode,
      desc: formSelection.formDesc,
      oid: formSelection.key,
      oidPath: formSelection.oidPath,
    } as FormTable;

    const obj = {
      ...objectSource,
      form: formTable,
      sheet: null,
      area: null,
      chart: null,
      path: formSelection.oidPath,
    } as TgkTableObjectSource;

    updateCoObjectSourceTable(obj, validateFields(obj), true);
  };

  const onFileClick = (formSelection: FormTreeSelection) => {
    sheetsAreaChartMap.clear();
    const formTable = {
      code: formSelection.formCode,
      desc: formSelection.formDesc,
      oid: formSelection.key,
      oidPath: formSelection.oidPath,
    } as FormTable;

    const obj = {
      ...objectSource,
      form: formTable,
      sheet: null,
      area: null,
      chart: null,
      path: formSelection.oidPath,
    } as TgkTableObjectSource;

    updateCoObjectSourceTable(obj, validateFields(obj), true);
  };

  function selectSheetComboBox(key?: string, text?: string) {
    if (key === undefined) {
      const obj = {
        ...objectSource,
        sheet: null,
        area: null,
        chart: null,
      } as TgkTableObjectSource;
      updateCoObjectSourceTable(obj, validateFields(obj), true);
      return;
    }
    const obj = {
      ...objectSource,
      sheet: { code: key, desc: text, uid: null },
      area: null,
      chart: null,
    } as TgkTableObjectSource;

    updateCoObjectSourceTable(obj, validateFields(obj), true);
    initAreasOrCharts({ code: key, desc: text } as SheetForm);
  }

  function selectAreaComboBox(key?: string) {
    if (key === undefined) {
      const obj = { ...objectSource, area: { name: null } } as TgkTableObjectSource;
      updateCoObjectSourceTable(obj, validateFields(obj), true);
      return;
    }
    const obj = { ...objectSource, area: { name: key } } as TgkTableObjectSource;
    updateCoObjectSourceTable(obj, validateFields(obj), true);
  }

  function selectChartComboBox(key?: string) {
    if (key === undefined) {
      const obj = { ...objectSource, chart: { name: null } } as TgkTableObjectSource;
      updateCoObjectSourceTable(obj, validateFields(obj), true);
      return;
    }
    const obj = { ...objectSource, chart: { name: key } } as TgkTableObjectSource;
    updateCoObjectSourceTable(obj, validateFields(obj), true);
  }

  function onToggleInheritableImageFormat(inheritFormatImage, imageExtension, imageResolution, modified = true) {
    const obj = {
      ...objectSource,
      inheritFormatImage: inheritFormatImage,
      formatImage: { imageExtension, imageResolution },
    } as TgkTableObjectSource;
    updateCoObjectSourceTable(obj, validateFields(obj), modified);
  }

  function handleChangeImageExtension(selectedKey, modified) {
    const obj = {
      ...objectSource,
      formatImage: { ...objectSource.formatImage, imageExtension: selectedKey },
    } as TgkTableObjectSource;
    updateCoObjectSourceTable(obj, validateFields(obj), modified);
  }

  function handleChangeImageResolution(selectedKey, modified) {
    const obj = {
      ...objectSource,
      formatImage: { ...objectSource.formatImage, imageResolution: selectedKey },
    } as TgkTableObjectSource;
    updateCoObjectSourceTable(obj, validateFields(obj), modified);
  }

  function validateFields(obj: TgkTableObjectSource): ObjectPanelTabs {
    let isValid = true;
    if (obj == null) isValid = false;
    else {
      const { dbApplication, form, sheet, area, chart } = obj;
      if (obj.objectSource !== SourceTypeEnum.EXCEL) {
        if (!dbApplication?.id) {
          isValid = false;
        }
      }
      if (!form?.oid || !sheet?.desc || (!area?.name && !chart?.name)) {
        isValid = false;
      }
    }
    return { key: props.tab.key, name: props.tab.name, isValid: isValid };
  }

  function onFormatAsImageChange(event: CheckboxChangeEvent) {
    const obj = {
      ...objectSource,
      elabResultType: event.value ? "IMAGE_STRINGBASE64" : "STRINGBASE64",
      inheritFormatImage: true,
    } as TgkTableObjectSource;
    updateCoObjectSourceTable(obj, validateFields(obj), true);
    if (props.isEdit) {
      if (!event.value) {
        setIsImageUnchecked(true);
      } else {
        setIsImageUnchecked(false);
      }
    }
  }

  const checkboxStyles = {
    marginRight: "5px",
  };

  const checkboxLabelStyles = {
    marginTop: "20px",
  };

  const checkboxLabelStylesDisabled = {
    marginTop: "20px",
    color: "rgb(161, 159, 157)",
  };

  const onChangeFormatAs = (_: React.SyntheticEvent<HTMLElement>, option: IChoiceGroupOption) => {
    const obj = {
      type: tableType,
      objectSource: String(option.key),
      dbApplication: null,
      form: null,
      sheet: null,
      area: null,
      path: null,
      elabResultType: tableType == "CHART" ? "IMAGE_STRINGBASE64" : "STRINGBASE64",
      inheritFormatImage: true,
      formatImage: null,
    } as TgkTableObjectSource;
    console.log("###CHANGING SOURCE FORM: " + String(option.key));
    updateCoObjectSourceTable(obj, validateFields(obj), true);
  };

  return {
    documentPartMetadata,
    objectSource,
    updateCoObjectSourceTable,
    tableType,
    sheetsAreaChartMap,
    onChangeFormatAs,
    onFormClick,
    selectDbComboBox,
    onFileClick,
    selectSheetComboBox,
    initAreasOrCharts,
    selectAreaComboBox,
    selectChartComboBox,
    onFormatAsImageChange,
    checkboxStyles,
    checkboxLabelStyles,
    onToggleInheritableImageFormat,
    handleChangeImageExtension,
    handleChangeImageResolution,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
    checkboxLabelStylesDisabled,
    isEdit,
    isComponentInstance,
    isLocked,
    isOldValueForAreaToHide,
    setIsOldValueForAreaToHide,
    hasSheetToBeSelected,
    setHasSheetToBeSelected,
    isLoadingDataExcel,
    setIsLoadingDataExcel,
    isImageUnchecked,
  };
};

export default useCoObjectSourceTablePanel;
